import { useCallback } from 'react';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';
import { useOpenSwiftOrders } from './useOpenSwiftOrders';
import { useOpenPerpMarketOrder } from './useOpenPerpMarketOrder';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import { useOpenPerpNonMarketOrder } from './useOpenPerpNonMarketOrder';
import useDriftClient from 'src/hooks/useDriftClient';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { updateAfterTx } from 'src/stores/DriftStore/helpers/transactionHelpers';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { safeCheckDevSwitchIsOn } from 'src/hooks/useDevSwitchIsOn';
import { UIMarket } from '@velocity-exchange/common';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import { ISOLATED_POSITION_RETRY_DEPOSIT_BUFFER_PCT } from 'src/constants/trade';

export const useOpenPerpOrder = () => {
	const isGeoBlocked = useDriftStore((s) => s.isGeoblocked);
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const appEventEmitter = useAppEventEmitter();
	const { openSwiftMarketOrder, openSwiftLimitOrder } = useOpenSwiftOrders();
	const openMarketOrder = useOpenPerpMarketOrder();
	const openNonMarketOrder = useOpenPerpNonMarketOrder();
	const { captureEvent } = usePostHogCapture();

	const openPerpOrder = useCallback(
		async (
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				leadSide: 'base' | 'quote';
			}
		): Promise<boolean> => {
			if (isGeoBlocked && !perpTradeFormOutputProps.reduceOnly) {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Drift from a restricted territory',
					lengthMs: 5000,
				});
				return false;
			}

			if (!driftClient || !currentUserAccount?.client) {
				console.error('Drift client or current user account is not available');
				return false;
			}

			// ensure that the current active user account is also the active subaccount in the drift client
			if (
				driftClient &&
				currentUserAccount &&
				driftClient.activeSubAccountId !== currentUserAccount.userId
			) {
				await driftClient.switchActiveUser(
					currentUserAccount.userId,
					currentUserAccount.authority
				);
			}

			const orderType = perpTradeFormOutputProps.orderType;

			if (!orderType) {
				notify({
					type: 'error',
					message: 'Order type is required',
					description: 'Please select an order type',
					lengthMs: 5000,
				});
				console.error('Order type is required');
				return false;
			}

			const nextOrderId =
				currentUserAccount?.client?.getUserAccountOrThrow().nextOrderId;
			let result = false;
			let swift_uuid: string | undefined;

			// Determine if we should use Swift - disable when additional isolated deposits are present
			// Swift only supports depositing into ONE isolated position, not multiple
			const hasAdditionalIsolatedDeposits =
				!!perpTradeFormOutputProps.additionalIsolatedPositionDeposits?.length;
			const canUseSwift =
				perpTradeFormOutputProps.isSwiftEnabled &&
				!hasAdditionalIsolatedDeposits;

			switch (orderType) {
				case 'market':
				case 'oracle':
					if (canUseSwift) {
						let swiftResult = await openSwiftMarketOrder(
							perpTradeFormOutputProps
						);

						// Retry once with 2% additional deposit buffer if InsufficientCollateral error on isolated position
						if (
							!swiftResult.sent &&
							swiftResult.retryWithDepositBuffer &&
							perpTradeFormOutputProps.isolatedPositionDeposit
						) {
							const bufferedDeposit =
								perpTradeFormOutputProps.isolatedPositionDeposit
									.muln(100 + ISOLATED_POSITION_RETRY_DEPOSIT_BUFFER_PCT)
									.divn(100);

							swiftResult = await openSwiftMarketOrder({
								...perpTradeFormOutputProps,
								isolatedPositionDeposit: bufferedDeposit,
							});
						}

						result = swiftResult.sent;
						swift_uuid = swiftResult.swiftUuid;
					} else {
						const txSig = await openMarketOrder(
							`${orderType}-${perpTradeFormOutputProps.side}-${Date.now()}`,
							perpTradeFormOutputProps
						);
						result = !!txSig;
					}
					break;
				case 'limit':
				case 'stopMarket':
				case 'stopLimit':
				case 'takeProfitMarket':
				case 'takeProfitLimit':
				case 'oracleLimit':
				case 'scaledOrders':
					if (canUseSwift && perpTradeFormOutputProps.orderType === 'limit') {
						let swiftResult = await openSwiftLimitOrder(
							perpTradeFormOutputProps
						);

						// Retry once with 2% additional deposit buffer if InsufficientCollateral error on isolated position
						if (
							!swiftResult.sent &&
							swiftResult.retryWithDepositBuffer &&
							perpTradeFormOutputProps.isolatedPositionDeposit
						) {
							const bufferedDeposit =
								perpTradeFormOutputProps.isolatedPositionDeposit
									.muln(100 + ISOLATED_POSITION_RETRY_DEPOSIT_BUFFER_PCT)
									.divn(100);

							swiftResult = await openSwiftLimitOrder({
								...perpTradeFormOutputProps,
								isolatedPositionDeposit: bufferedDeposit,
							});
						}

						result = swiftResult.sent;
						swift_uuid = swiftResult.swiftUuid;
					} else {
						const toastId = `${orderType}-${
							perpTradeFormOutputProps.side
						}-${Date.now()}`;
						const txSig = await openNonMarketOrder(toastId, {
							...perpTradeFormOutputProps,
							orderType, // we override the order type here to ensure it is not undefined
						});
						result = !!txSig;
					}
					break;
				default: {
					console.error(`Unhandled order type: ${orderType}`);
					const exhaustiveCheck: never = orderType;
					throw new Error(`Unhandled order type: ${exhaustiveCheck}`);
				}
			}

			if (result) {
				updateAfterTx(driftClient, currentUserAccount.client);

				appEventEmitter.emit('tradeConfirmation');
			}

			const uiMarket = UIMarket.createPerpMarket(
				perpTradeFormOutputProps.targetMarketIndex
			);

			captureEvent('trade_placed', {
				market_type: 'perps',
				order_type: orderType,
				direction: perpTradeFormOutputProps.side,
				market_symbol: uiMarket.market.symbol,
				trade_base: +(perpTradeFormOutputProps.baseSizeStringValue ?? 0),
				trade_notional: +(perpTradeFormOutputProps.quoteSizeStringValue ?? 0),
				geolocation_history: useDriftStore.getState().geolocationHistory,
				is_new_account: nextOrderId === 1,
				next_order_id: nextOrderId,
				is_devmode: safeCheckDevSwitchIsOn(),
				is_successful: result,
				swift_uuid: swift_uuid,
			});

			return result;
		},
		[
			captureEvent,
			openSwiftMarketOrder,
			openSwiftLimitOrder,
			openMarketOrder,
			openNonMarketOrder,
			isGeoBlocked,
			driftClient,
			currentUserAccount,
		]
	);

	return openPerpOrder;
};
